import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { LoaderOrEmpty, Error404, Error500 } from '@agencypmg/alli';
import AccessDenied from './AccessDenied';
import getClient from '../getClient';
import setCurrentClient from '../setCurrentClient';

/**
 * this component is rendered at the root route and will take care of setting
 * a current client if one is not already set by another, higher priority route
 */
export default function SetClientFromUrl({ localCurrentClient, currentUser }) {
    // this ref is set immediately on response from the central API with the current
    // client's slug then cleared when we clear the loader. The idea here is that
    // if we get a client ID in the url, as soon as we navigate to the url with
    // the slug it's going to trigger a re-render. We don't want to double fetch
    // the client in those cases.
    const currentRequest = useRef(null);

    const { clientIdOrSlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // fire the use effect event whenever the client slug changes
    useEffect(() => {
        // if we're currently making a request to the same client slug in the URL,
        // then we don't need to make another one.
        if (
            currentRequest.current &&
            currentRequest.current === clientIdOrSlug
        ) {
            return;
        }

        // if we've got no current client OR the current client doesn't match
        // we need to trigger the loader AND set the current client to null.
        // Setting the client to null will trigger other applications to not
        // render anything yet.
        if (!localCurrentClient || localCurrentClient.slug !== clientIdOrSlug) {
            setCurrentClient(null);
            setLoading(true);
        }
        setError(null);

        getClient(clientIdOrSlug)
            .then(
                client => {
                    currentRequest.current = client.slug;

                    // if we got a client ID in the URL, we can navigate to the "pretty"
                    // url for the user :rocket:
                    if (client.slug && client.slug !== clientIdOrSlug) {
                        navigate(
                            {
                                pathname: location.pathname.replace(
                                    clientIdOrSlug,
                                    client.slug,
                                ),
                                search: location.search,
                                hash: location.hash,
                            },
                            {
                                replace: true,
                            },
                        );
                    }

                    // and finally let the rest of the single UI know about this new client
                    setCurrentClient(client);

                    return client;
                },
                err => {
                    // eslint-disable-next-line no-undef
                    if (process.env.NO_LOGS !== '1') {
                        console.error(err);
                    }
                    setCurrentClient(null); // erase the current client on error so no client apps render.
                    setError(err.response?.status || 500);
                },
            )
            .then(() => {
                currentRequest.current = null;
                setLoading(false);
            });
    }, [clientIdOrSlug]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return <LoaderOrEmpty loaderWhen={loading} emptyWhen={false} />;
    }

    if (!error) {
        return null;
    }

    switch (error) {
        case 403:
            return (
                <AccessDenied
                    currentUser={currentUser}
                    clientIdOrSlug={clientIdOrSlug}
                />
            );
        case 404:
            return <Error404 />;
        default:
            return <Error500 />;
    }
}
